import type {
  PrCommentNode,
  PrCommentsChangeRequest,
  ScmContributor,
} from '../../../../api/unified-process-graph-client/unified-process-graph-client.type';
import type { CommentItem, CommentNeighbourIndices } from './pr-comment-panel.type';

/**
 * Keeps only http(s) links, so a stored URL can never become a `javascript:` link.
 *
 * @param {string | null | undefined} url - A URL from the API
 * @returns {string | undefined} The URL when it is http(s), otherwise undefined
 */
const safeHttpUrl = (url: string | null | undefined): string | undefined =>
  url && /^https?:\/\//i.test(url) ? url : undefined;

/**
 * Picks the name to show for an SCM contributor: display name, then login.
 *
 * @param {ScmContributor | null | undefined} contributor - The contributor from the API
 * @returns {string | undefined} The name to show, or undefined when neither is set
 */
const contributorName = (contributor: ScmContributor | null | undefined): string | undefined =>
  contributor?.name || contributor?.login || undefined;

/**
 * Flattens the PR's comment nodes into one navigator list, keeping each
 * comment paired with its node so the arrows can cross between reviews.
 *
 * @param {PrCommentNode[]} prComments - Every comment node on the PR, in time order
 * @returns {CommentItem[]} Every comment on the PR, in node order then comment order
 */
const buildCommentItems = (prComments: PrCommentNode[]): CommentItem[] =>
  prComments.flatMap((node) => node.comments.map((detail) => ({ node, detail })));

/**
 * Finds the comment the panel shows. It follows the opened node: the comment
 * the arrows last picked when it belongs to that node, otherwise the node's
 * first comment, so a graph click on another node starts at its first comment.
 *
 * @param {CommentItem[]} items - The navigator list from `buildCommentItems`
 * @param {string | null} activeDetailId - The comment id the arrows last picked, if any
 * @param {string} nodeId - The id of the opened comment node
 * @returns {number} The index of the shown comment, or -1 when the node has no comment text
 */
const resolveActiveCommentIndex = (items: CommentItem[], activeDetailId: string | null, nodeId: string): number => {
  const chosen = items.findIndex((item) => item.detail.id === activeDetailId && item.node.id === nodeId);
  return chosen >= 0 ? chosen : items.findIndex((item) => item.node.id === nodeId);
};

/**
 * Works out where the previous and next arrows go. With a comment shown they
 * step one comment either way; when the opened node has no comment text they
 * step to the nearest comment on an earlier or later node. `items` must come
 * from `buildCommentItems(prComments)` with this same array, since nodes are
 * matched by reference.
 *
 * @param {CommentItem[]} items - The navigator list from `buildCommentItems`
 * @param {PrCommentNode[]} prComments - Every comment node on the PR, in time order
 * @param {string} nodeId - The id of the opened comment node
 * @param {number} activeIndex - The shown comment's index from `resolveActiveCommentIndex`
 * @returns {CommentNeighbourIndices} The arrow targets; -1 or `items.length` means none
 */
const resolveNeighbourIndices = (
  items: CommentItem[],
  prComments: PrCommentNode[],
  nodeId: string,
  activeIndex: number,
): CommentNeighbourIndices => {
  if (activeIndex >= 0) return { previousIndex: activeIndex - 1, nextIndex: activeIndex + 1 };
  const nodeIndex = prComments.findIndex((node) => node.id === nodeId);
  const nodeIndexOf = (item: CommentItem) => prComments.indexOf(item.node);
  return {
    previousIndex: items.findLastIndex((item) => nodeIndexOf(item) < nodeIndex),
    nextIndex: items.findIndex((item) => nodeIndexOf(item) > nodeIndex),
  };
};

/**
 * Builds the header subtitle, e.g. `#412 by The Octocat`. Only shown once the
 * full PR has loaded with a title, since the graph node has neither.
 *
 * @param {PrCommentsChangeRequest | undefined} changeRequest - The PR from the PR comments endpoint
 * @returns {string | null} The subtitle; null before the PR loads or when it has no title, and
 *   an empty string when it has a title but neither a number nor an author name
 */
const buildPrSubtitle = (changeRequest: PrCommentsChangeRequest | undefined): string | null => {
  if (!changeRequest?.title) return null;
  const author = contributorName(changeRequest.author);
  return [changeRequest.number ? `#${changeRequest.number}` : null, author ? `by ${author}` : null]
    .filter(Boolean)
    .join(' ');
};

export {
  buildCommentItems,
  buildPrSubtitle,
  contributorName,
  resolveActiveCommentIndex,
  resolveNeighbourIndices,
  safeHttpUrl,
};
